import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';

import {
  AbsenceEntry,
  AbsenceHistory,
  AbsenceType,
} from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useEmployeeAbsenceHistoricalData } from '~/data/absences/useEmployeeAbsenceHistoricalData';
import { useEmployeeAbsencesChangeHistory } from '~/data/absences/useEmployeeAbsencesChangeHistory';
import { isDateWithinRange } from '~/utils/helpers/date';

export enum AbsenceHistoryRowType {
  Delete = 'delete',
  Coming = 'coming',
  Ongoing = 'ongoing',
  Past = 'past',
}

type PropsType = {
  employeeId: number;
  absenceType: AbsenceType;
};

type UseAbsenceHistoryDataOutput = {
  isLoading: boolean;
  absencesHistory: AbsenceHistory<AbsenceEntry>[];
  getHistoryRowType: (entry: AbsenceEntry) => AbsenceHistoryRowType;
  historical: {
    isLoading: boolean;
    showHistorical: boolean;
    setShowHistorical: (show: boolean) => void;
  };
};

export const useAbsenceHistoryData = (
  props: PropsType,
): UseAbsenceHistoryDataOutput => {
  const { employeeId, absenceType } = props;

  const { absenceEntries } = useEmployeeAbsenceData({
    employeeId,
    includeDeleted: true,
  });

  const { entries } = useMemo(() => {
    return {
      entries: absenceEntries.filter((e) => e.type === absenceType && e.uid),
    };
  }, [absenceType, absenceEntries]);

  const { isLoading, absencesHistory } = useEmployeeAbsencesChangeHistory({
    employeeId,
    absenceEntries: entries,
  });

  const [showHistorical, setShowHistorical] = useState<boolean>(false);

  const { isLoading: isLoadingHistorical, absenceEntries: historicalEntries } =
    useEmployeeAbsenceHistoricalData({
      employeeId,
      absenceType,
      loadHistorical: showHistorical,
    });

  const {
    isLoading: isLoadingHistoricalHistory,
    absencesHistory: historicalHistory,
  } = useEmployeeAbsencesChangeHistory({
    employeeId,
    absenceEntries: historicalEntries,
  });

  const getHistoryRowType = useCallback(
    (entry: AbsenceEntry): AbsenceHistoryRowType => {
      const { supersededReason, startDate, endDate } = entry;

      if (supersededReason === 'DELETE') {
        return AbsenceHistoryRowType.Delete;
      }

      if (
        (!!startDate &&
          !!endDate &&
          isDateWithinRange(new Date(), startDate, endDate)) ||
        (!!startDate &&
          !endDate &&
          moment(startDate).isSameOrBefore(new Date(), 'day'))
      ) {
        return AbsenceHistoryRowType.Ongoing;
      }

      if (moment(startDate).isAfter(new Date(), 'day')) {
        return AbsenceHistoryRowType.Coming;
      }

      return AbsenceHistoryRowType.Past;
    },
    [],
  );

  return {
    isLoading,
    absencesHistory: [...absencesHistory, ...historicalHistory],
    getHistoryRowType,
    historical: {
      isLoading: isLoadingHistorical || isLoadingHistoricalHistory,
      showHistorical,
      setShowHistorical,
    },
  };
};
